import {
  MixpanelEvent,
  MixpanelEventSource,
  MixpanelSourceProvider,
  tabLabelerEnumerated,
  useMixpanel,
} from '@talos/kyoko';
import { memo, useCallback, useMemo } from 'react';
import type { TradeTabProps } from '../../../../types/LayoutConfig';
import { DEFAULT_TRADE_TABS, NEW_TRADE_TAB_DEFAULTS } from '../../../Blotters/Trades/defaults';
import { TRADES_BLOTTER_PREFIX } from '../../../Blotters/Trades/tokens';
import {
  MobileBlotterPanel,
  type MobileBlotterPanelAddTabOption,
  type MobileBlotterPanelProps,
  type MobileBlotterPanelRenderTabArgs,
} from './MobileBlotterPanel';
import { MobileFilteredTrades } from './Trades/MobileFilteredTrades';

/**
 * Mobile trades panel. Resolves the active tab and renders `MobileFilteredTrades`,
 * which mirrors the desktop `FilteredTrades` data pipeline (column defs, filter
 * logic, persisted layout) but renders via `MobileDataTable` + `MobileTradeCard`.
 *
 * The `blotterID` matches the desktop convention (`principal/trades/<tabId>`)
 * so persisted column layout / filter state is shared across desktop and mobile.
 */
export const MobileTradesBlotter = memo(function MobileTradesBlotter(
  props: Omit<MobileBlotterPanelProps<TradeTabProps>, 'defaultTabs' | 'renderTab'>
) {
  const mixpanel = useMixpanel(MixpanelEventSource.TradesBlotter);
  const tabLabeler = useMemo(() => tabLabelerEnumerated<TradeTabProps>('Trades'), []);
  const renderTab = useCallback(
    ({ tab, tabId }: MobileBlotterPanelRenderTabArgs<TradeTabProps>) => (
      <MobileFilteredTrades
        key={tabId}
        blotterID={`${TRADES_BLOTTER_PREFIX}/${tabId}`}
        tabLabel={tab.label}
        defaultFilter={tab.defaultFilter ?? NEW_TRADE_TAB_DEFAULTS.defaultFilter}
        defaultColumns={tab.defaultColumns ?? NEW_TRADE_TAB_DEFAULTS.defaultColumns}
      />
    ),
    []
  );

  const addTabOptions = useMemo<MobileBlotterPanelAddTabOption<TradeTabProps>[]>(
    () => [
      {
        id: 'trades',
        label: 'Trades',
        createTab: sourceTabs => {
          const newTab = { ...NEW_TRADE_TAB_DEFAULTS };
          return {
            ...newTab,
            label: tabLabeler(newTab, [...sourceTabs], 'creation'),
          };
        },
        onAdd: () => mixpanel.track(MixpanelEvent.AddTab),
      },
    ],
    [mixpanel, tabLabeler]
  );

  return (
    <MixpanelSourceProvider value={MixpanelEventSource.TradesBlotter}>
      <MobileBlotterPanel<TradeTabProps>
        {...props}
        defaultTabs={DEFAULT_TRADE_TABS}
        renderTab={renderTab}
        addTabOptions={addTabOptions}
      />
    </MixpanelSourceProvider>
  );
});
